"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, Search, MapPin, Target, ChartBar as BarChart3, Building2, Compass, ArrowRight, ShieldCheck, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const belfastMetrics = [{
  id: "m1",
  label: "Local Search Growth",
  value: "+38%",
  subtext: "Year-over-year increase in high-intent Belfast commercial searches",
  tag: "High Growth"
}, {
  id: "m2",
  label: "Google Maps Pack Competition",
  value: "74/100",
  subtext: "Contested top-3 positions across BT1-BT15 postcode service areas",
  tag: "Competitive"
}, {
  id: "m3",
  label: "Mobile Lookup Share",
  value: "68%",
  subtext: "Belfast local service searches originating on smartphones",
  tag: "Mobile First"
}, {
  id: "m4",
  label: "AI Overview Rate",
  value: "41%",
  subtext: "Searches displaying AI Search Overviews in Northern Ireland",
  tag: "AI Era Search"
}];
const trendData = [{
  month: "Jan",
  b2bVolume: 2400,
  commercialVolume: 1800,
  techVolume: 1200
}, {
  month: "Feb",
  b2bVolume: 2600,
  commercialVolume: 1950,
  techVolume: 1350
}, {
  month: "Mar",
  b2bVolume: 2900,
  commercialVolume: 2100,
  techVolume: 1500
}, {
  month: "Apr",
  b2bVolume: 3100,
  commercialVolume: 2200,
  techVolume: 1750
}, {
  month: "May",
  b2bVolume: 3400,
  commercialVolume: 2400,
  techVolume: 2000
}, {
  month: "Jun",
  b2bVolume: 3800,
  commercialVolume: 2650,
  techVolume: 2300
}];
const chartConfig = {
  b2bVolume: {
    label: "B2B Services",
    color: "hsl(var(--primary))"
  },
  commercialVolume: {
    label: "Local Commercial",
    color: "hsl(var(--accent))"
  },
  techVolume: {
    label: "Tech & Innovation",
    color: "hsl(var(--chart-4))"
  }
};
const localKeywords = [{
  id: "k1",
  cluster: "Professional Services",
  keyword: "belfast business consultancy",
  volume: "1,600 / mo",
  intent: "Transactional",
  opportunity: "Competitors lack structured local landing pages and targeted technical schema."
}, {
  id: "k2",
  cluster: "Commercial & Trades",
  keyword: "commercial services belfast",
  volume: "2,400 / mo",
  intent: "High Commercial",
  opportunity: "Google Business Profiles in BT1-BT9 are under-optimized for local reviews."
}, {
  id: "k3",
  cluster: "B2B & Tech Sector",
  keyword: "b2b software belfast",
  volume: "1,200 / mo",
  intent: "Investigation",
  opportunity: "Substantial content gaps in mid-funnel comparison and solution guides."
}, {
  id: "k4",
  cluster: "Ecommerce & Logistics",
  keyword: "belfast online delivery services",
  volume: "3,100 / mo",
  intent: "Transactional",
  opportunity: "Slow page load times and missing product snippet structured data."
}];
const marketOpportunities = [{
  id: "o1",
  title: "Google Map Pack Dominance",
  description: "I structure citations, geo-targeted landing pages, and local schema so your business secures top 3 visibility across all major Belfast postcodes.",
  badge: "Local Visibility"
}, {
  id: "o2",
  title: "High-Intent B2B Lead Hubs",
  description: "I build tailored content hubs that target Belfast decision-makers directly when they actively evaluate specialist B2B suppliers.",
  badge: "Direct Leads"
}, {
  id: "o3",
  title: "AI Search Optimization",
  description: "I optimize entity references so your Belfast brand is featured directly inside Google Gemini and AI-driven search experiences.",
  badge: "Future Ready"
}];
const regionalCities = [{
  id: "c1",
  cityName: "Warrington Local SEO",
  path: "/LocalSeoConsultantWarringtonPage",
  description: "Industrial and local commercial search strategies."
}, {
  id: "c2",
  cityName: "Preston Search Strategy",
  path: "/LocalSeoConsultantPrestonPage",
  description: "Targeted organic search growth for Lancashire businesses."
}, {
  id: "c3",
  cityName: "Sunderland Local SEO",
  path: "/LocalSeoConsultantSunderlandPage",
  description: "Technical SEO and local pack ranking optimization."
}, {
  id: "c4",
  cityName: "Doncaster SEO Consultant",
  path: "/LocalSeoConsultantDoncasterPage",
  description: "High-converting B2B search lead generation."
}, {
  id: "c5",
  cityName: "Hull Organic Growth",
  path: "/LocalSeoConsultantHullPage",
  description: "Ecommerce and local organic search solutions."
}, {
  id: "c6",
  cityName: "Wakefield SEO Services",
  path: "/LocalSeoConsultantWakefieldPage",
  description: "Tailored SEO campaigns for regional enterprises."
}];
export default function Belfastmarketanalysis() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState<"trends" | "keywords" | "opportunities">("trends");
  return <section id="belfastmarketanalysis" data-nav="light" className="py-20 md:py-28 bg-background text-foreground relative overflow-hidden">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[350px] bg-secondary/30 rounded-full blur-3xl -z-10 pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge variant="secondary" className="mb-4 gap-1.5 py-1 px-3.5 text-xs rounded-full font-medium">
            <MapPin className="size-3.5 text-primary" />
            <span>Belfast Search Intelligence</span>
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default mb-6">
            Belfast SEO &amp; Search Market Analysis
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            When analyzing the Belfast search landscape, I evaluate clear local intent, competitor friction points, and untapped search volume. Work directly with me to transform these local search trends into sustained revenue for your business.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {belfastMetrics.map((metric, i) => <Card key={metric.id} data-index={i} className="border border-border/80 bg-card/60 backdrop-blur-xs shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {metric.label}
                  </span>
                  <Badge variant="outline" className="text-[11.5px] px-2 py-0.5 rounded-full border-border">
                    {metric.tag}
                  </Badge>
                </div>
                <CardTitle className="text-3xl sm:text-4xl font-bold text-primary font-default">
                  {metric.value}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-xs text-muted-foreground leading-normal">
                  {metric.subtext}
                </CardDescription>
              </CardContent>
            </Card>)}
        </div>

        <Card className="border border-border shadow-md bg-card overflow-hidden mb-16">
          <CardHeader className="bg-muted/40 border-b border-border p-6 sm:p-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div>
                <CardTitle className="text-xl sm:text-2xl font-semibold text-foreground font-default">
                  Belfast Search Dynamics &amp; Keyword Opportunities
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground mt-1">
                  Data-driven breakdown of regional search query volume, intent, and actionable growth vectors.
                </CardDescription>
              </div>

              <div className="flex flex-wrap items-center gap-2 bg-background p-1.5 rounded-lg border border-border">
                <Button variant={activeTab === "trends" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("trends")} className="rounded-md text-xs font-medium cursor-pointer">
                  <TrendingUp className="size-3.5 mr-1.5" />
                  Search Trends
                </Button>
                <Button variant={activeTab === "keywords" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("keywords")} className="rounded-md text-xs font-medium cursor-pointer">
                  <Search className="size-3.5 mr-1.5" />
                  Key Keywords
                </Button>
                <Button variant={activeTab === "opportunities" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("opportunities")} className="rounded-md text-xs font-medium cursor-pointer">
                  <Target className="size-3.5 mr-1.5" />
                  Strategic Plan
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-6 sm:p-8">
            {activeTab === "trends" && <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <BarChart3 className="size-4 text-primary" />
                    <span>Monthly Belfast Search Volume Growth by Sector</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <span className="size-2.5 rounded-full bg-primary inline-block" /> B2B Services
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="size-2.5 rounded-full bg-accent inline-block" /> Commercial
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="size-2.5 rounded-full bg-chart-4 inline-block" /> Tech
                    </span>
                  </div>
                </div>

                <ChartContainer config={chartConfig} className="h-[280px] sm:h-[320px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={trendData} margin={{
                  top: 10,
                  right: 10,
                  left: -20,
                  bottom: 0
                }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border/60" vertical={false} />
                      <XAxis dataKey="month" tickLine={false} axisLine={false} className="text-xs fill-muted-foreground" />
                      <YAxis tickLine={false} axisLine={false} className="text-xs fill-muted-foreground" />
                      <Tooltip content={<ChartTooltipContent indicator="dot" />} />
                      <Area type="monotone" dataKey="b2bVolume" stroke="var(--color-b2bVolume)" fill="var(--color-b2bVolume)" fillOpacity={0.15} strokeWidth={2} />
                      <Area type="monotone" dataKey="commercialVolume" stroke="var(--color-commercialVolume)" fill="var(--color-commercialVolume)" fillOpacity={0.15} strokeWidth={2} />
                      <Area type="monotone" dataKey="techVolume" stroke="var(--color-techVolume)" fill="var(--color-techVolume)" fillOpacity={0.15} strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartContainer>

                <p className="text-xs text-muted-foreground italic text-center pt-2">
                  * Analysis reflects aggregate organic search volume indices across the greater Belfast Metropolitan area.
                </p>
              </motion.div>}

            {activeTab === "keywords" && <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {localKeywords.map((kw, i) => <div key={kw.id} data-index={i} className="p-5 rounded-lg border border-border bg-background hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                    <div className="flex items-center justify-between mb-3">
                      <Badge variant="secondary" className="text-[12.5px] font-medium">
                        {kw.cluster}
                      </Badge>
                      <span className="text-xs font-semibold text-primary">{kw.volume}</span>
                    </div>
                    <h3 className="text-base font-semibold text-foreground font-default mb-1">
                      {kw.keyword}
                    </h3>
                    <p className="text-xs text-muted-foreground mb-3">
                      Search Intent: <span className="font-medium text-foreground">{kw.intent}</span>
                    </p>
                    <div className="p-3 rounded-md bg-muted/50 border border-border/60 text-xs text-muted-foreground">
                      <span className="font-semibold text-foreground">Gap &amp; Opportunity: </span>
                      {kw.opportunity}
                    </div>
                  </div>)}
              </motion.div>}

            {activeTab === "opportunities" && <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {marketOpportunities.map((opp, i) => <div key={opp.id} data-index={i} className="p-6 rounded-xl border border-border bg-background flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                          <Sparkles className="size-5" />
                        </div>
                        <Badge variant="outline" className="text-[11.5px] font-medium border-primary/30 text-primary">
                          {opp.badge}
                        </Badge>
                      </div>
                      <h3 className="text-lg font-semibold text-foreground font-default mb-2">
                        {opp.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                        {opp.description}
                      </p>
                    </div>
                    <div className="flex items-center text-xs font-semibold text-primary">
                      <span>Direct Execution Plan</span>
                      <ArrowRight className="size-3.5 ml-1" />
                    </div>
                  </div>)}
              </motion.div>}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch mb-20">
          <Card className="lg:col-span-2 border border-border bg-card p-6 sm:p-8 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck className="size-5 text-primary" />
                <h3 className="text-xl font-semibold text-foreground font-default">
                  Why Work Directly With Me on Your Belfast SEO
                </h3>
              </div>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
                Most agencies delegate local Belfast campaigns to junior account managers who rely on generic templates. When you work with me, you get direct, hands-on access to an experienced independent SEO consultant. I personally write the strategy, fix technical friction, and execute local optimizations that win top rankings.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                <div className="flex items-start gap-2 text-xs text-foreground font-medium">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Direct Consultant Access (No Account Managers)</span>
                </div>
                <div className="flex items-start gap-2 text-xs text-foreground font-medium">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Bespoke Belfast &amp; NI Local Schema Optimization</span>
                </div>
                <div className="flex items-start gap-2 text-xs text-foreground font-medium">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Transparent Monthly Deliverables &amp; Clear ROI</span>
                </div>
                <div className="flex items-start gap-2 text-xs text-foreground font-medium">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Future-Proof AI Search &amp; Entity GEO Integration</span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4 pt-4 border-t border-border">
              <Button asChild variant="default" size="default" className="rounded-md cursor-pointer">
                <Link to="/ContactPage">Book a Free Belfast SEO Consultation</Link>
              </Button>
              <Button asChild variant="outline" size="default" className="rounded-md cursor-pointer">
                <Link to="/CaseStudiesPage">View Verified Case Studies</Link>
              </Button>
            </div>
          </Card>

          <Card className="border border-border bg-primary text-primary-foreground p-6 sm:p-8 flex flex-col justify-between relative overflow-hidden">
            <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
              <Compass className="size-40" />
            </div>
            <div>
              <Badge variant="secondary" className="mb-4 text-[12.5px] bg-primary-foreground/15 text-primary-foreground border-none">
                Independent SEO Consultancy
              </Badge>
              <h3 className="text-2xl font-bold font-default mb-3 leading-tight">
                Need National or International Reach Beyond Belfast?
              </h3>
              <p className="text-sm opacity-90 leading-relaxed mb-6">
                Whether you are expanding from Northern Ireland into the broader UK market or targeting global audiences, I design scalable, multi-region search strategies tailored to your enterprise.
              </p>
            </div>
            <Button asChild variant="secondary" size="default" className="w-full justify-between cursor-pointer">
              <Link to="/LocalSEOPage">
                <span>Explore Local SEO Framework</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </Card>
        </div>

        <Separator className="my-16" />

        <div>
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
            <div>
              <div className="flex items-center gap-2 text-primary text-xs font-semibold uppercase tracking-wider mb-2">
                <Building2 className="size-4" />
                <span>Geographic Reach &amp; Regional Expertise</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-semibold text-foreground font-default">
                Explore Other Regional Local SEO Locations
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              In addition to Belfast, I deliver bespoke local search optimization across major UK economic centers and hubs.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {regionalCities.map((city, i) => <Card key={city.id} data-index={i} className="border border-border/80 bg-card hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold text-foreground font-default flex items-center justify-between">
                    <span>{city.cityName}</span>
                    <MapPin className="size-4 text-muted-foreground" />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-xs text-muted-foreground leading-normal">
                    {city.description}
                  </p>
                  <Button asChild variant="ghost" size="sm" className="px-0 text-primary hover:bg-transparent hover:underline cursor-pointer">
                    <Link to={city.path} className="inline-flex items-center text-xs font-medium gap-1">
                      <span>View Location Strategy</span>
                      <ArrowRight className="size-3" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </div>
    </section>;
}